import {
    Component,
    Element,
    Host,
    h,
    Listen,
    State,
    Watch,
} from "@stencil/core";

/**
 * The `<ux-iws-nb-all-in-one>` component represents the Nimbus "all-in-one" module.
 * It is wrapper and controller for multiple all-in-one tiles and detail groups.
 *
 * @slot (default) - Slot for the tiles and detail groups (`<ux-iws-nb-all-in-one-tile>`, `<ux-iws-nb-all-in-one-detail-group>`)
 */
@Component({
    tag: "ux-iws-nb-all-in-one",
    styleUrl: "nb-all-in-one.scss",
    shadow: false,
})
export class NbAllInOne {
    /**
     * Stores references to the detail group elements within the component.
     */
    private detailGroups: HTMLUxIwsNbAllInOneDetailGroupElement[] = [];

    /**
     * Reference to the host element of the component.
     */
    @Element() host: HTMLUxIwsNbAllInOneElement;

    /**
     * Holds the currently active group name.
     */
    @State() activeGroup: string = "";

    /**
     * Event listener for the "uxShow" event. Sets the active group based on the event detail.
     * @param event - CustomEvent containing the name of the group to be activated.
     */
    @Listen("uxShow")
    onGroupShow(event: CustomEvent<string>) {
        const activeGroupName = event.detail;
        if (!activeGroupName) {
            return;
        }
        this.detailGroups = this.getDetailGroups();
        const activeGroup = this.detailGroups.find(
            (group) => group.group === activeGroupName
        );
        this.activeGroup = activeGroup ? activeGroupName : "";
    }

    /**
     * Event listener for the "uxHide" event. Clears the active group if it matches the event detail.
     * @param event - CustomEvent containing the name of the group to be deactivated.
     */
    @Listen("uxHide")
    onGroupHide(event: CustomEvent<string>) {
        const activeGroupName = event.detail;
        if (activeGroupName && activeGroupName === this.activeGroup) {
            this.activeGroup = "";
        }
    }

    /**
     * Watches for changes to the activeGroup state and updates the active property of each detail group accordingly.
     */
    @Watch("activeGroup")
    setActiveGroup() {
        this.detailGroups = this.getDetailGroups();
        this.detailGroups.forEach((group) => {
            group.active = group.group === this.activeGroup;
        });
    }

    /**
     * Retrieves all detail group elements within the component.
     * @returns An array of HTMLUxIwsNbAllInOneDetailGroupElement instances.
     */
    private getDetailGroups = (): HTMLUxIwsNbAllInOneDetailGroupElement[] => {
        return Array.from(
            this.host.querySelectorAll<HTMLUxIwsNbAllInOneDetailGroupElement>(
                "ux-iws-nb-all-in-one-detail-group"
            )
        );
    };

    componentWillLoad() {
        this.setActiveGroup();
    }

    componentDidLoad() {
        this.setActiveGroup();
    }

    render() {
        return (
            <Host>
                <slot />
            </Host>
        );
    }
}
